import { action, computed, observable, ObservableMap, ObservableSet } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { toast, ToastContainer } from 'react-toastify';

import { DbKey, makeWithDbId, WithDbId } from '../../../../../adl-gen/common/db';
import {
  Competency,
  makeWorkRoleSkill,
  Skill,
  WorkRole,
  WorkRoleSkill
} from '../../../../../adl-gen/totalinteraction/futurefit/db';
import {
  ActionType,
  Available,
  ChangeState,
  makeAssigned,
  makeRoleBuilderData,
  makeUnassigned,
  RoleBuilderData,
  WorkRoleListEntry
} from '../../../../../adl-gen/totalinteraction/futurefit/frontend';
import { WorkRoleReferenceData } from '../../../../../adl-gen/totalinteraction/futurefit/requests';
import { withI18n, WithI18nProps } from '../../../../../internationalization/i18n-hoc';
import { PlotKeyToggleState } from '../../../../../stores/plot-key-toggle-state';
import { SelectOption } from '../../../../../ui/widgets/select/select';
import { uniqueId } from '../../../../../util/utils';
import { ChangesMap, LoadedMap, RoleBuilderHelper, WorkRoleStore } from '../../../../stores';
import { MultiSelectObserver, renderButtons } from '../../../common/editor';
import { toastOptions } from '../../../common/form';
import { RoleBuilderEditor } from '../../editors/role-builder-editor/role-builder-editor';
import { RoleBuilderCompetencyRadar } from '../../role-builder-competency-radar/role-builder-competency-radar';

const styles = require('../../../common/editor.css');

/** RoleSkillList props definitions */
interface RoleBuilderFormProps extends WithI18nProps {
  // TODO: Sort out these items
  //   - WorkRoleQuizData[]:
  //     - Quizzes (for WorkRole)
  //     - UserQuizzes
  // Suggest moving the Competencies and AvailableSkills into lookupData
  // Suggest converting WorkRole to WorkRoleListEntry
  // Suggest adding SkillTrainingOffering and CompetencyTrainingOffering here
  data: RoleBuilderData;

  // lookupData: Pertaining to all WorkRoles
  //   - List of AppClients (for superuser)
  //   - List of LPs (on client providerIds list, or all for superuser)
  //   - List of WorkRoleScoreRanges (?)
  //   - List of Quizzes (?)
  // Suggest adding TrainingOfferings here
  lookupData: WorkRoleReferenceData;

  store: WorkRoleStore;

  selectedRole: WorkRoleListEntry;
  onSelectRole(r: DbKey<WorkRole>): void;
  onCancel?(): void;

  debug?: boolean;
}

@observer
class RoleBuilderForm0 extends React.Component<RoleBuilderFormProps> {
  @observable
  helper: RoleBuilderHelper;

  @observable changeState: ObservableMap<DbKey<Skill>, ChangeState<WorkRoleSkill>> = observable.map(new Map(), {
    deep: false
  });
  // Loaded state - not observable - readonly
  readonly loadedState: LoadedMap = new Map();

  /// Manage Competency selector
  @observable includedCompetencies: ObservableSet<DbKey<Competency>> = observable.set(new Set());

  // @observable quizList: QuizListEntry[] = []

  /// Control nav when Save As... clicked
  @observable.ref saveAsRole: boolean = false;

  @observable.ref savedNewRole?: WorkRoleListEntry;

  /// Not used currently - prevent click on Save/Cancel when processing
  @observable locked = false;
  @action lockUpdates = (onOff?: boolean) => (onOff === undefined ? (this.locked = true) : (this.locked = onOff));

  constructor(props: Readonly<RoleBuilderFormProps>) {
    super(props);
    this.helper = this.loadData(props.selectedRole, props.data);
  }

  @action
  loadData = (workrole: WorkRoleListEntry, data: RoleBuilderData): RoleBuilderHelper => {
    // /// Now populate the display arrays and initialize selected
    // /// Only display competencies already assigned
    // // If the Competency has any assigned skills, add it to the (Competency->Available WorkRoleSkill[]) map
    /// Init loadedState
    for (const availableSkill of data.availableSkills) {
      // if (availableSkill.kind === "assigned") {
      //   const { item, attribute } = availableSkill.value
      //   this.loadedState.set(attribute.id, { ...attribute.value , ...item })
      // }
      /// Technically possible that the array has >1 item for same skill - just overwrite
      this.loadedState.set(availableSkill.value.attribute.id, availableSkill);
    }

    for (const competencyId of data.competencies.map(c => c.id)) {
      const availableForComp = data.availableSkills
        .filter(s => s.kind === 'assigned')
        .map(s => s.value.attribute.value)
        .filter(s => s.competencyId === competencyId);
      if (availableForComp.length > 0) {
        this.includedCompetencies.add(competencyId);
      }
    }

    const initData = {
      ...data,
      competencies: data.competencies.slice().filter(c => this.includedCompetencies.has(c.id))
    };

    /// Init
    return RoleBuilderHelper.fromRoleBuilderData(workrole, initData);
  };

  CompetencyListObserver = observer(() => {
    return MultiSelectObserver({
      options: this.availableCompetencies(),
      selectedOptions: this.selectedCompetencies(),
      title: 'Competencies assigned to Role',
      xsSpan: 4,
      onChange: this.onSetCompetencies,
      debug: false
    });
  });

  availableCompetencies = (): SelectOption[] => {
    const { t } = this.props;
    // Go back to the 'source'
    return this.props.data.competencies.map(competency => ({
      label: t(competency.value.fullName),
      value: competency.id
    }));
  };

  selectedCompetencies = (): SelectOption[] => {
    const options: SelectOption[] = [];
    for (const option of this.availableCompetencies()) {
      if (this.includedCompetencies.has(option.value)) {
        options.push(option);
      }
    }
    return options;
  };

  @action
  onSetCompetencies = (selectedOptions: SelectOption[]) => {
    /// If selection is empty, clear list, after dropping already-assigned skills...
    if (selectedOptions.length === 0) {
      // Should force an update
      this.includedCompetencies.forEach(this.deleteWorkRoleSkillsDroppedCompetency);
      this.includedCompetencies.clear();
    } else {
      selectedOptions.forEach(opt => this.includedCompetencies.add(`${opt.value}`));
      // Check if option is now missing, if so, delete, after dropping skills...
      this.includedCompetencies.forEach(id => {
        if (!selectedOptions.map(opt => opt.value).includes(id)) {
          // Should force an update
          this.deleteWorkRoleSkillsDroppedCompetency(id);
          this.includedCompetencies.delete(id);
        }
      });
      // Finally, check for 'new' competencies - in the list, but with
      // no assigned skills - and add 'new' changes for all linked skills
      // so that they default to 'on'
      this.includedCompetencies.forEach(id => {
        const assigned = this.helper.getAssignedSkillIdsByCompetency(id);
        if (assigned === undefined) {
          // Should always be the case...
          const availableSkills = this.helper.getAvailableSkillsForCompetency(id);
          for (const available of availableSkills) {
            this.logUpdate(available.value.attribute.id, {
              kind: 'new',
              value: makeWorkRoleSkill({
                workRoleId: this.props.selectedRole.id,
                skillId: available.value.attribute.id,
                expected: 0,
                industryAverage: 0,
                // default for now
                skillGroupHeadings: new Array<string>()
              })
            });
          }
        } // ... If it's not, set them up here (check against loadedState first)
      });
    }
    // TODO:
    this.reInitHelper();
  };

  @action
  deleteWorkRoleSkillsDroppedCompetency = (id: DbKey<Competency>) => {
    const { data } = this.props;
    const availableSkillsForCompetency = data.availableSkills.filter(s => s.value.attribute.value.competencyId === id);
    for (const s of availableSkillsForCompetency) {
      const { attribute: skill } = s.value;
      // const wrs = this.helper.getWorkRoleSkillforSkill(skill.id)
      const loaded = this.loadedState.get(skill.id);
      if (loaded !== undefined && loaded.kind === 'assigned') {
        this.logUpdate(skill.id, { kind: 'delete', value: loaded.value.item.id });
        // Also - check if any unposted
      } else if (this.changeState.has(skill.id)) {
        this.changeState.delete(skill.id);
      }
    }
  };

  // Not working
  @action
  deleteCompetencyIfNoAssignedSkillsLeft = () => {
    /// Go through list in selector
    this.includedCompetencies.forEach(cid => {
      // Accumulator for assigned skills
      const assignedSkillsForCompetency = [];

      for (const [id, loaded] of this.loadedState.entries()) {
        if (loaded.value.attribute.value.competencyId === cid) {
          if (loaded.kind === 'assigned') {
            /// Item was assigned when loaded; has it subsequently been deleted?
            const change = this.changeState.get(id);
            if (change && change.kind !== 'delete') {
              /// Item still assigned; add to list
              assignedSkillsForCompetency.push(id);
            } else if (change === undefined) {
              /// Item still assigned
              assignedSkillsForCompetency.push(id);
            }
          }
        }
      }

      // Accumulator should now be empty if (a) no assigned skills
      // to start with or (b) last remaining assigned skills deleted
      if (assignedSkillsForCompetency.length === 0) {
        // remove from selector
        this.includedCompetencies.delete(cid);
      }
    });
  };

  @action
  logUpdateAndReinitHelper = (id: DbKey<Skill>, item: ChangeState<WorkRoleSkill>) => {
    this.logUpdate(id, item);
    this.reInitHelper();
  };

  /// NB May well be called more than once...
  @action
  logUpdate = (id: DbKey<Skill>, item: ChangeState<WorkRoleSkill>) => {
    this.changeState.set(id, item);
    if (item.kind === 'delete') {
      // TODO: Removes from accordion list, radar and includedCompetencies, *but*
      // TODO: doesn't remove from visible multiselect list
      this.deleteCompetencyIfNoAssignedSkillsLeft();
    }
  };

  @action
  onCancel = () => {
    // this.errors = []
    this.saveAsRole = false;
    this.savedNewRole = undefined;
    this.lockUpdates(false);
    this.changeState.clear();
    this.reInitHelper();
    if (this.props.onCancel) {
      this.props.onCancel();
    }
  };

  @computed
  get inputIsValid(): boolean {
    const changeState = Array.from(this.changeState.entries());
    return changeState.every(([k, v]) => {
      const wrs = this.helper.getWorkRoleSkillforSkill(k);
      switch (v.kind) {
        /// NEW: editor adds dummy WRS
        case 'new':
          return wrs !== undefined;
        case 'delete':
          return wrs === undefined;
        case 'update':
          return wrs !== undefined;
      }
    });
  }

  /// Just read the changeState array
  onSave = async (openToastId?: React.ReactText) => {
    const { store, selectedRole } = this.props;

    const updates: ChangesMap = Array.from(this.changeState).reduce((acc: ChangesMap, [skillId, change]) => {
      /// Check changes against original loaded items - deal with multiple
      /// in-form updates (eg. add/remove or remove/add back)
      /// assigned   + 'new' -> convert to 'update'
      /// unassigned + 'delete' -> drop the delete altogether
      /// unassigned + 'new' + set new value -> convert 'update' to 'new'
      const loaded = this.loadedState.get(skillId);
      let result = change;

      if (loaded && loaded.kind === 'assigned') {
        switch (change.kind) {
          case 'new': {
            result = { kind: 'update', value: { id: loaded.value.item.id, value: change.value } };
            acc[skillId] = result;
            return acc;
          }
          case 'update': {
            if (change.value.id === '') {
              result = { kind: 'update', value: { id: loaded.value.item.id, value: change.value.value } };
              acc[skillId] = result;
              return acc;
            }
          }
          case 'delete': {
            if (change.value === '') {
              result = { kind: 'delete', value: loaded.value.item.id };
              acc[skillId] = result;
              return acc;
            }
          }
        }
      }

      if (loaded === undefined || loaded.kind === 'unassigned') {
        if (change.kind === 'delete') {
          return acc;
        }

        if (change.kind === 'update') {
          result = { kind: 'new', value: change.value.value };
          acc[skillId] = result;
          return acc;
        }
      }

      /// not assigned on load; new
      acc[skillId] = change;
      return acc;
    }, {});

    const role = selectedRole;

    const toastId = openToastId ? openToastId : toast.loading('Please wait...', { ...toastOptions, autoClose: 3000 });
    try {
      await store.updateRoleBuilderData({
        roleId: role.id,
        updates
      });
      toast.dismiss(toastId);

      if (this.changeState.size > 0) {
        const changeState = Array.from(this.changeState.values());
        const messages = [];

        const createCount = this.countActions(changeState, ActionType.create);
        if (createCount > 0) {
          messages.push(`${createCount} Skill(s) assigned.`);
        }
        const updateCount = this.countActions(changeState, ActionType.update);
        if (updateCount > 0) {
          messages.push(`${updateCount} Skill(s) updated.`);
        }
        const deleteCount = this.countActions(changeState, ActionType.delete);
        if (deleteCount > 0) {
          messages.push(`${deleteCount} Skill(s) unassigned.`);
        }
        toast.success(
          `Role update actions completed successfully.
        ${messages}`,
          { ...toastOptions, onClose: this.onCancel, autoClose: 3000 }
        );
      }
    } catch (e) {
      toast.dismiss(toastId);
      toast.error(`Role ${role.name.value.en} create/update failed: ${(e as Error).message}`, {
        ...toastOptions,
        onClose: this.onCancel
      });
    }
  };

  countActions = (changeState: ChangeState<WorkRoleSkill>[], a: ActionType) => {
    const total = changeState.reduce((count, item) => {
      if (
        (item.kind === 'new' && a === ActionType.create) ||
        (item.kind === 'update' && a === ActionType.update) ||
        (item.kind === 'delete' && a === ActionType.delete)
      ) {
        return count + 1;
      } else {
        return count;
      }
    }, 0);
    return total;
  };

  @action
  reInitHelper = () => {
    const { data, selectedRole } = this.props;
    const competencies = data.competencies.slice().filter(k => this.includedCompetencies.has(k.id));

    // TODO: So far, unchanged
    const quizzes = data.quizzes.slice();
    const activeData = makeRoleBuilderData({
      role: selectedRole.id,
      availableSkills: this.mergeAvailable(),
      competencies,
      quizzes
    });
    // reinit helper
    this.helper = RoleBuilderHelper.fromRoleBuilderData(selectedRole, activeData);
  };

  convertChangeToAvailable = (
    skill: WithDbId<Skill>,
    change: ChangeState<WorkRoleSkill>
  ): Available<Skill, WorkRoleSkill> => {
    switch (change.kind) {
      case 'new':
        return {
          kind: 'assigned',
          value: makeAssigned({
            /// Just make up an ID - ensure it isn't posted to the server
            item: makeWithDbId({ id: `WRS-${uniqueId()}`, value: change.value }),
            attribute: skill
          })
        };
      case 'update':
        return {
          kind: 'assigned',
          value: makeAssigned({
            item: change.value,
            attribute: skill
          })
        };
      case 'delete':
        return {
          kind: 'unassigned',
          value: makeUnassigned({ attribute: skill })
        };
    }
  };

  availableFromChangeState = (): Available<Skill, WorkRoleSkill>[] => {
    const result: Available<Skill, WorkRoleSkill>[] = [];
    for (const [k, v] of this.changeState.entries()) {
      const skill = this.props.data.availableSkills.find(sk => sk.value.attribute.id === k);
      if (skill) {
        result.push(this.convertChangeToAvailable(makeWithDbId({ id: k, value: skill.value.attribute.value }), v));
      }
    }
    return result;
  };

  mergeAvailable = (): Available<Skill, WorkRoleSkill>[] => {
    const result: Available<Skill, WorkRoleSkill>[] = this.props.data.availableSkills.slice();
    const changes = this.availableFromChangeState();
    if (changes.length > 0) {
      // updates/deletes
      const updates = result.map(item => {
        const change = changes.find(ch => ch.value.attribute.id === item.value.attribute.id);
        return change ? change : item;
      });
      return updates;
    }
    return result;
  };

  renderRadar = () => {
    // cf storeFactory.makePlotKeyToggleStore
    // TODO: This is meant to go into state and passed down through props - but otoh
    // TODO: we are not using plotKeys (yet - till we do role comparisons)
    const plotKeyToggleState: PlotKeyToggleState = new PlotKeyToggleState();
    // Hide "actual"
    plotKeyToggleState.toggle('actual');

    return (
      // TODO: CSS & relative sizing
      <div style={{ width: 300, height: 300 }}>
        <RoleBuilderCompetencyRadar
          helper={this.helper}
          plotKeyToggleState={plotKeyToggleState}
          // onCompetencyClick={TODO:}
          // selectedCompetency={this.selectedCompetency}
          gridRadii={this.helper.setRadii()}
          viewBox={{ width: 300, height: 300 }}
        />
      </div>
    );
  };

  renderAll = () => {
    const { debug, selectedRole, t } = this.props;

    return (
      <div className={styles.page}>
        <div className={styles.body}>
          <div className={styles.row}>
            <h4>Role: {t(selectedRole.name)}</h4>
            {renderButtons(
              this.onSave,
              undefined,
              this.onCancel,
              () => this.inputIsValid && !this.locked,
              undefined,
              () => this.inputIsValid && !this.locked
            )}
          </div>
        </div>
        <div className={styles.bodyNoOverflow}>
          <div className={styles.row}>
            <this.CompetencyListObserver />
          </div>
          <div className={styles.row}>{this.renderRadar()}</div>
        </div>
        <div className={styles.body}>
          <div className={styles.row}>
            <RoleBuilderEditor
              loadedSkills={this.loadedState}
              helper={this.helper}
              logUpdates={this.logUpdateAndReinitHelper}
              debug={debug}
            />
          </div>
        </div>
      </div>
    );
  };

  render() {
    return (
      <React.Fragment>
        <ToastContainer newestOnTop />
        {this.renderAll()}
      </React.Fragment>
    );
  }
}

export const RoleBuilderForm = withI18n(RoleBuilderForm0);



// WEBPACK FOOTER //
// ./src/admin/ui/components/forms/role-builder-form/role-builder-form.tsx